import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NuqsAdapter } from "nuqs/adapters/react";

import {
  AnalyticsPageType,
  AnalyticsEventName,
  ShopifySalesChannel,
  useShopifyCookies,
  sendShopifyAnalytics,
  getClientBrowserParameters,
} from "@shopify/hydrogen";

import { Redirect } from "@bared/ui";

import type { GatsbyBrowser } from "~/types/gatsby";

import { Cart } from "~/components/Cart";
import { Header } from "~/components/Header";
import { Footer } from "~/components/Footer";
import { LiveChatScripts } from "~/components/LiveChatScripts";
import { GeofencingModal } from "~/components/GeofencingModal";
import { ImpactAnalytics } from "~/components/ImpactAnalytics";
import { AnalyticsSubscriber } from "~/components/AnalyticsSubscriber";
import { SkipLinks } from "~/components/Accessibility/SkipLink/SkipLink";

import { UIProvider } from "~/providers/UIProvider";
import { SanityProvider } from "~/providers/SanityProvider";
import { ServerDataProvider } from "~/providers/ServerDataProvider";

import { resolveShop } from "~/lib/shopify";
import type { ServerResponseContext, RedirectResponse } from "~/lib/server";

import "swiper/css/bundle";

import "./src/tailwind.css";

/**
 * Polyfill Object.hasOwn for older browsers.
 * @see https://bared-melbourne.slack.com/archives/C0434TNDGNQ/p1733790230813129
 */
import "@ungap/has-own";

const queryClient = new QueryClient();

const shop = resolveShop();

/**
 * Wrap page with providers and UI elements.
 *
 * __Why not use wrapRootElement for providers?__
 * When putting providers in wrapRootElement, they all get rendered twice on every page,
 * because Gatsby wraps not only the page, but also the Head export separately.
 * @see https://github.com/gatsbyjs/gatsby/issues/38269
 */
export const wrapPageElement: GatsbyBrowser["wrapPageElement"] = ({
  props,
  element,
}) => {
  // Manage Shopify Analytics Cookie
  useShopifyCookies({ hasUserConsent: true });

  const serverData = props.serverData as any;

  const serverResponseContext = serverData?.context as ServerResponseContext;

  if (!serverResponseContext) {
    throw new Error(
      "Could not resolve server response context in gatsby-browser.tsx. Ensure your page exports a getServerData function and you are using the respond method from server context.",
    );
  }

  // If server returned a redirect, do not render.
  if (serverData && [301, 302].includes((serverData as any).status)) {
    const location = (serverData as RedirectResponse["props"]).location;
    return <Redirect href={location} />;
  }

  /**
   * Resolve gender from server data.
   * This is present on the PDP and PLP templates.
   * @todo Move this to ServerDataProvider and access with useGender hook.
   */
  const gender = (() => {
    const gender =
      serverData?.["collectionGender"] || serverData?.["productGender"];

    if (!gender || "string" !== typeof gender) return null;

    // There are a variety of values like "Womens", "Women's", "Women" etc.
    if (gender.toLowerCase().includes("women")) return "women";
    if (gender.toLowerCase().includes("men")) return "men";

    return null;
  })();

  const transparentHeader = true === serverData["transparentHeader"];

  return (
    <NuqsAdapter>
      <QueryClientProvider client={queryClient}>
        <ServerDataProvider
          rootData={serverResponseContext.rootData}
          siteData={serverResponseContext.siteLoaderData}
        >
          <SanityProvider>
            <UIProvider>
              <SkipLinks />
              <Cart />
              <Header gender={gender} transparent={transparentHeader} />
              <main className="pt-[--header-height]" id="content-main">
                {element}
              </main>
              <Footer />
              <GeofencingModal />
              <LiveChatScripts />
            </UIProvider>
            <AnalyticsSubscriber />
            <ImpactAnalytics />
          </SanityProvider>
        </ServerDataProvider>
      </QueryClientProvider>
    </NuqsAdapter>
  );
};

// Is the current navigation triggered by a browser event, like the back button.
// Normal Gatsby navigations do not trigger popstate events, so this will be false for those.
let isBrowserNavigation = false;

// Keep track of the interval which searches for product cards.
let scrollRestorationInterval: number = 0;

export const onRouteUpdate: GatsbyBrowser["onRouteUpdate"] = ({
  location,
  prevLocation,
}) => {
  // Prevent the browser from interfering with scroll restoration.
  window.history.scrollRestoration = "manual";

  // For all page navigations except the one that is triggered by a hash change, scroll to the top.
  if (!!prevLocation && !location.hash) {
    window.requestAnimationFrame(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    });
  }

  // Clear any existing scroll restoration interval.
  if (0 < scrollRestorationInterval) {
    window.clearInterval(scrollRestorationInterval);
  }

  // When moving from a product to a collection via the back button, attempt to scroll to the product card.
  if (
    isBrowserNavigation &&
    location.pathname.includes("/collections/") &&
    prevLocation?.pathname.includes("/products/")
  ) {
    // Extract the product handle from the path.
    const productHandle = prevLocation.pathname.match(/([^/]+)\/?$/)?.[1];

    // No product handle found, do nothing.
    if (!productHandle) return;

    // Reset attempts.
    let scrollRestorationAttempts = 0;

    // Watch the page until this product card appears.
    scrollRestorationInterval = window.setInterval(() => {
      scrollRestorationAttempts++;

      const productCard = document.querySelector(
        `a[data-product-handle="${productHandle}"]`,
      );

      // Product card found, scroll to it.
      if (productCard) {
        window.requestAnimationFrame(() => {
          productCard.scrollIntoView({ behavior: "smooth" });
        });
        window.clearInterval(scrollRestorationInterval);
        return;
      }

      // Try for a maximum of 25 attempts (5 seconds).
      if (scrollRestorationAttempts >= 25) {
        window.clearInterval(scrollRestorationInterval);
      }
    }, 200);
  }

  // Reset the browser navigation flag.
  isBrowserNavigation = false;

  // Emit Shopify Analytics page view.
  try {
    const pageType = ((): AnalyticsPageType[keyof AnalyticsPageType] => {
      const globalPath = location.pathname.replace(/^\/[a-z]{2}(\/|$)/, "/");

      if ("/" === globalPath) {
        return AnalyticsPageType.home;
      }

      if (globalPath.startsWith("/collections/")) {
        return AnalyticsPageType.collection;
      }

      if (globalPath.startsWith("/products/")) {
        return AnalyticsPageType.product;
      }

      if (globalPath.startsWith("/articles/")) {
        return AnalyticsPageType.article;
      }

      if (globalPath.startsWith("/search/")) {
        return AnalyticsPageType.search;
      }

      return AnalyticsPageType.page;
    })();

    sendShopifyAnalytics({
      eventName: AnalyticsEventName.PAGE_VIEW,
      payload: {
        pageType,
        currency: "AUD",
        hasUserConsent: true,
        shopId: `gid://shopify/Shop/${shop.shopId}`,
        shopifySalesChannel: ShopifySalesChannel.headless,
        ...getClientBrowserParameters(),
      },
    });
  } catch (e) {
    console.error("Could not send Shopify Analytics event", e);
    // Do not throw to avoid breaking route change.
  }
};

declare global {
  interface Window {
    isScrolled: boolean;
  }
}

export const onClientEntry: GatsbyBrowser["onClientEntry"] = () => {
  if (!window) return;

  // This runs when a real browser navigation occurs, like the back button.
  // The isBrowserNavigation flag is used in onRouteUpdate to detect the back button.
  window.addEventListener("popstate", () => {
    isBrowserNavigation = true;
  });

  window.isScrolled = false;

  window.addEventListener("scroll", () => {
    const scrolledNow = window.scrollY > 0;

    if (scrolledNow !== window.isScrolled) {
      document.body.classList.toggle("is-scrolled", scrolledNow);
      window.isScrolled = scrolledNow;
    }
  });

  /**
   * Allow legacy onClick handlers in Sanity to open the live chat widget.
   * https://bared-melbourne.slack.com/archives/C045GT6BNAU/p1750731474708389
   */
  window["LiveChat"] = {
    open() {
      if (!window["zE"]) return;
      window["zE"]("messenger", "open");
    },
  };

  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      const reasonsToForceReload = [
        /loading chunk \d* failed./i,
        /we couldn't find the correct component chunk with the name component--src/i,
      ];

      const hasValidErrors = reasonsToForceReload.reduce(
        (prev, curr) => prev || curr.test(event.reason),
        false,
      );

      if (!hasValidErrors) {
        return;
      }

      const forceReloadKey = "__fr";
      const queryStringParams = new URLSearchParams(window.location.search);
      const queryStringKeyValues = [...queryStringParams.entries()];
      const hasForceReloadParam = queryStringKeyValues.find(
        ([key]) => key === forceReloadKey,
      );
      const newQueryString = [
        ...queryStringKeyValues,
        [forceReloadKey, Date.now()],
      ].reduce(
        (prev, [key, value]) =>
          `${prev}${prev === "?" ? "" : "&"}${key}=${value}`,
        "?",
      );

      if (!hasForceReloadParam) {
        window.location.href = `${window.location.protocol}//${window.location.host}${window.location.pathname}${newQueryString}`;
      }
    },
  );
};

/**
 * Disable Gatsby's native "scroll restoration" behavior.
 *
 * This behavior remembers the scroll position of each page, and when
 * landing on it, returns to that scroll position.
 *
 * It sometimes does not work correctly when moving between products as
 * it sets the scroll position to the same as the previous product,
 * ending up half way down the page.
 *
 * Note the Gatsby docs say you can return coordinates from this hook
 * but it actually only works with a boolean. Coordinates are ignored.
 *
 * @see https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/#shouldUpdateScroll
 * @see https://github.com/gatsbyjs/gatsby/blob/master/packages/gatsby-react-router-scroll/src/scroll-handler.tsx
 */
export const shouldUpdateScroll: GatsbyBrowser["shouldUpdateScroll"] = () =>
  false;
